import Errored from './Errored';
import PropTypes from 'prop-types';
import React from 'react';
import GS from '../grafsync/grafsync';
import Loading from './Loading';
import { getGraphFromProject, makeCancelable } from './utils/utils';

const loadOptions = {};

class Associations extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      loadInfo: null,
      isErrored: null
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.collectResourceData();
  }

  loadData(iriData, options) {
    this.cancellable = makeCancelable(GS.loadDataFromGraph(iriData, options));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  collectResourceData = async () => {
    try {
      if (!this.props.ontologyProjectAssociation || this.props.ontologyProjectAssociation.length === 0) {
        this.setState({ loadInfo: [], isLoading: false });
        return;
      }
      // First we load association resources (these are in the project graph)
      const opaLoadData = this.props.ontologyProjectAssociation.map(opa => ({
        graph: this.props.graph,
        iri: opa
      }));
      await this.loadData(opaLoadData, {}).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      // We get all the association information
      const opaSet = this.props.ontologyProjectAssociation
        .filter(opa => GS.isLoaded(opa, this.props.graph))
        .map(opa => GS.data(opa, this.props.graph));
      // There is an edge case where the same IRI is in two ontologies but totally unlikely
      let loadInfo = [];
      let apInfo = [];
      let templateInfo = [];
      opaSet.forEach(opa => {
        const graphIRI = getGraphFromProject(opa.associatedProject);
        // There might be associated properties and we record the associagted project graph here
        if (opa.associatedProperty && opa.associatedProperty.length > 0) {
          opa.associatedProperty.forEach(ap => {
            loadInfo.push({
              graph: graphIRI,
              iri: ap
            });
            apInfo.push(ap);
          });
        }
        // There might be associated templates and again we record the associated graph
        if (opa.associatedTemplate && opa.associatedTemplate.length > 0) {
          opa.associatedTemplate.forEach(at => templateInfo.push({
            graph: this.props.graph,
            associatedProjectGraph: graphIRI,
            iri: at
          }));
        }
      });

      if (templateInfo.length > 0) {
        // Now load property shapes for any templates
        let propsList = [];
        await this.loadData(templateInfo, loadOptions).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        // Get full list of SHACL properties for all templates
        templateInfo.forEach(info => {
          const template = info.iri;
          if (GS.isLoaded(template, this.props.graph)) {
            const templateData = GS.data(template, this.props.graph);
            if (templateData.shProperty && templateData.shProperty.length > 0) {
              templateData.shProperty.forEach(shProp => propsList.push({
                graph: this.props.graph,
                associatedProjectGraph: info.associatedProjectGraph,
                iri: shProp
              }));
            }
          }
        });

        // And now load the properties for all property shapes
        await this.loadData(propsList, loadOptions).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        propsList.forEach(info => {
          const shProp = info.iri;
          if (GS.isLoaded(shProp, this.props.graph)) {
            const shPropData = GS.data(shProp, this.props.graph);
            const associatedProjectGraph = info.associatedProjectGraph;
            const alreadyInList = loadInfo.some(il => il.graph === associatedProjectGraph && il.iri === shPropData.shPath);
            if (!alreadyInList) {
              loadInfo.push({
                graph: associatedProjectGraph,
                iri: shPropData.shPath,
                isTemplateProp: !apInfo.includes(shPropData.shPath)
              });
            }
          }
        });
      }

      // Now load the props data from the relevant graphs
      // If user doesn't have permissions for ontology projects the label and type info will be missing
      await this.loadData(loadInfo, loadOptions).promise;
      if (this.cancellable && this.cancellable.isCancelled()) return;
      loadInfo.forEach(info => {
        if (GS.isLoaded(info.iri, info.graph)) {
          const data = GS.data(info.iri, info.graph);
          info.label = data.label;
          if (data.domain) info.domain = data.domain;
          if (data.range) info.range = data.range;
          info.type = data.type;
        }
      });
      this.setState({ loadInfo, isLoading: false });
      if (this.props.onLoad) this.props.onLoad(loadInfo);
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  render() {

    const { isLoading, loadInfo, isErrored } = this.state;
    const { component } = this.props;
    const WrappedComponent = component;

    if (isErrored) return <Errored data={isErrored} />;
    if (isLoading) return <Loading />;

    return (
      <WrappedComponent
        loadInfo={loadInfo}
        {...this.props} />
    );
  }
}

Associations.propTypes = {
  onLoad: PropTypes.func,
  isProcessing: PropTypes.bool,
  component: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object
  ]),
  graph: PropTypes.string.isRequired,
  ontologyProjectAssociation: PropTypes.array
};

export default Associations;